import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createAppointment, fetchMyPatients, consultations } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import "./CreateMeeting.css";

export default function CreateMeeting({ onCreate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [patientId, setPatientId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [mode, setMode] = useState("video"); // video | in-person
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [, setFetchingPatients] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchMyPatients()
      .then(data => {
        setPatients(data);
        setFetchingPatients(false);
      })
      .catch(err => {
        setErr("Failed to load patients");
        setFetchingPatients(false);
      });
  }, []);

  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter(
      (p) => {
        const name = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
        return name.includes(q) || (p.email && p.email.toLowerCase().includes(q));
      }
    );
  }, [patients, search]);

  const selectedPatient = useMemo(
    () => patients.find((p) => p.id === patientId) || null,
    [patientId, patients]
  );

  function validate() {
    if (user?.role === 'doctor') {
      if (!patientId) return "Select a patient.";
      if (!date) return "Pick a date.";
      if (!time) return "Pick a time.";
    }
    return "";
  }

  async function handleCreate(e) {
    e.preventDefault();
    const v = validate();
    if (v) return setErr(v);
    setErr("");
    setLoading(true);

    try {
      if (user?.role === 'patient') {
        // Patient Flow: Request Consultation
        const result = await consultations.request();
        if (onCreate) onCreate(result);
        alert(t("createMeeting.request_success") || "Consultation requested successfully! An admin will schedule it shortly.");
        navigate('/appointments');
      } else {
        // Doctor Flow: Create Appointment directly
        const payload = {
          patientId: patientId,
          date,
          time,
          duration: 60, // Default duration
          mode,
          reason: reason.trim(),
          notes: notes.trim()
        };
        const result = await createAppointment(payload);
        if (onCreate) onCreate(result);

        // Clear form
        setPatientId("");
        setSearch("");
        setDate("");
        setTime("");
        setMode("video");
        setReason("");
        setNotes("");
        alert("Meeting created successfully!");
        navigate('/meetingdashboard');
      }

    } catch (error) {
      setErr("Failed to create meeting: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }

  const { t } = useTranslation();

  return (
    <div className="cm-shell">
      <div className="cm-head">
        <div>
          <h1 className="cm-title">{t("createMeeting.title")}</h1>
          <p className="cm-sub">{t("createMeeting.sub")}</p>
        </div>
        <div className="cm-doc">
          <span className="cm-label">{t("createMeeting.doctor")}</span>
          <span className="cm-doc-name">{user?.email?.split('@')[0]}</span>
        </div>
      </div>

      <form className="cm-form" onSubmit={handleCreate}>
        {/* Patient picker (Only for doctors) */}
        {user?.role === 'doctor' ? (
          <div className="cm-field">
            <label className="cm-label" htmlFor="patient">Patient</label>
            <div className="cm-patient-picker">
              <input
                id="patient-search"
                className="cm-input"
                placeholder={t("createMeeting.search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
              />
              <select
                id="patient"
                className="cm-select"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
              >
                <option value="">{t("createMeeting.select_patient")}</option>
                {options.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.first_name} {p.last_name} {p.email ? `— ${p.email}` : ""}
                  </option>
                ))}
              </select>
            </div>
            {selectedPatient && (
              <div className="cm-chip">
                <span className="cm-avatar">{
                  (selectedPatient.first_name?.[0] || '') + (selectedPatient.last_name?.[0] || '')
                }</span>
                <span>{selectedPatient.first_name} {selectedPatient.last_name}</span>
                {selectedPatient.email && <span className="cm-dot" />}
                {selectedPatient.email && <span className="cm-muted">{selectedPatient.email}</span>}
              </div>
            )}
          </div>
        ) : (
          <div className="cm-field">
            <label className="cm-label">Request for</label>
            <div className="cm-doc-name" style={{ border: '1px solid #ddd', padding: '12px', borderRadius: '8px', background: '#f9f9f9' }}>
              My Health Assessment
            </div>
          </div>
        )}

        {/* Date/Time - Only for Doctors */}
        {user?.role === 'doctor' && (
          <>
            <div className="cm-grid-2">
              <div className="cm-field">
                <label className="cm-label" htmlFor="date">{t("createMeeting.date")}</label>
                <input
                  id="date"
                  type="date"
                  className="cm-input"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="cm-field">
                <label className="cm-label" htmlFor="time">{t("createMeeting.time")}</label>
                <input
                  id="time"
                  type="time"
                  className="cm-input"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
            </div>

            {/* Mode */}
            <div className="cm-field">
              <label className="cm-label">{t("createMeeting.mode")}</label>
              <div className="cm-seg">
                <button
                  type="button"
                  className={`cm-seg-btn ${mode === "video" ? "is-active" : ""}`}
                  onClick={() => setMode("video")}
                >
                  {t("createMeeting.video")}
                </button>
                <button
                  type="button"
                  className={`cm-seg-btn ${mode === "in-person" ? "is-active" : ""}`}
                  onClick={() => setMode("in-person")}
                >
                  {t("createMeeting.in_person")}
                </button>
              </div>
            </div>

            {/* Reason / Notes */}
            <div className="cm-field">
              <label className="cm-label" htmlFor="reason">{t("createMeeting.reason")}</label>
              <input
                id="reason"
                className="cm-input"
                placeholder={t("createMeeting.reason_placeholder")}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <div className="cm-field">
              <label className="cm-label" htmlFor="notes">{t("createMeeting.notes")}</label>
              <textarea
                id="notes"
                className="cm-textarea"
                rows={5}
                placeholder={t("createMeeting.notes")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </>
        )}

        {user?.role === 'patient' && (
          <div className="cm-info-box" style={{ padding: '20px', background: '#eef2ff', borderRadius: '8px', marginBottom: '20px', color: '#4f46e5' }}>
            <p><strong>👋 Ready to start?</strong></p>
            <p>Click the button below to request a consultation. Our team will review your request and schedule an appointment for you. You will be notified once a date is set.</p>
          </div>
        )}

        {err && <div className="cm-error">{err}</div>}

        <div className="cm-actions">
          <button type="submit" className="cm-btn cm-btn-primary" disabled={loading}>
            {loading ? t("createMeeting.creating") : (user?.role === 'patient' ? "Send Request" : t("createMeeting.create_btn"))}
          </button>
          <button
            type="button"
            className="cm-btn cm-btn-ghost"
            onClick={() => {
              setPatientId("");
              setSearch("");
              setDate("");
              setTime("");
              setMode("video");
              setReason("");
              setNotes("");
              setErr("");
            }}
          >
            {t("createMeeting.reset")}
          </button>
        </div>
      </form>
    </div>
  );
}
